import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DxFormComponent } from 'devextreme-angular';
import { Observable, Subject, Subscription } from 'rxjs';
import { featureGroup, FeatureGroup, Map, Polygon } from 'leaflet';
import { Crop, Field, FieldCrop } from 'shared';
import areaIcon from '@iconify/icons-mdi/ruler';
import drawIcon from '@iconify/icons-mdi/vector-polygon';
import editIcon from '@iconify/icons-mdi/edit-outline';
import { finalize, first } from 'rxjs/operators';
import { area } from '@turf/turf';
import { success } from '@pnotify/core';
import { FieldCropService } from '../../stores/field-crop.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import * as L from 'leaflet';
import DataSource from 'devextreme/data/data_source';
import { CropService } from '../../stores/crop.service';
import { cloneDeep } from 'lodash';

@Component({
  selector: 'app-field-editor-map-field-crop-control',
  templateUrl: './field-editor-map-field-crop-control.component.html',
  styleUrls: ['./field-editor-map-field-crop-control.component.scss'],
  animations: [
    trigger('editCropMode', [
      state('false', style({
        transform: 'translateY(-100px)'
      })),
      state('true', style({
        transform: 'translateY(0px)'
      })),
      transition('* => *', animate(250)),
    ])
  ]
})
export class FieldEditorMapFieldCropControlComponent implements OnInit, OnDestroy {

  @ViewChild(DxFormComponent)
  form: DxFormComponent;

  @Input()
  mapReady: Observable<Map>;

  @Input()
  editEvent: Observable<{ layer: FeatureGroup, field: Field, fieldCrop: FieldCrop }>;

  @Output()
  editDone = new EventEmitter<{ field: Field, isDeleted: boolean }>();

  @Input()
  crops: DataSource;

  oldField: Field;
  oldFieldCrop: FieldCrop;
  field?: Field;
  fieldCrop?: FieldCrop;

  map: Map;

  changes: Partial<FieldCrop> = {};

  @HostBinding('@editCropMode')
  editingEnabled = false;

  private editEventSub: Subscription;
  editLayer: FeatureGroup;

  areaIcon = areaIcon;
  closeButtonOptions: any;
  drawButtonOptions: any;
  formCancelBtnOptions: any;
  editButtonOptions: any;

  formSaveBtnOptions: any;
  @HostBinding('class.shakeit')
  shake: boolean;
  formVisible: boolean;
  loadingVisible: boolean;
  formTrackChanges: boolean;
  cropsGridVisible = false;
  corpsItems?: Crop[];
  fieldCropDynamicInputs = [];

  items: Crop[];
  ds: DataSource;

  constructor(private fieldCropService: FieldCropService, private service: CropService,) {
    this.ds = this.service.newDataSource({
      paginate: false,
    });

    this.ds.store().on('loaded', (result) => {
      this.items = result.data;
    });

    this.closeButtonOptions = {
      icon: 'close',
      hint: 'Fermer',
      onClick: () => {
        this.disableEditing();
      }
    };

    this.drawButtonOptions = {
      icon: `<svg  width="18px" height="18px" viewBox="0 0 24 24">${drawIcon.body}</svg>`,
      hint: 'Dessiner',
      onClick: () => {
        this.startDrawing();
      }
    };

    this.editButtonOptions = {
      icon: `<svg  width="18px" height="18px" viewBox="0 0 24 24">${editIcon.body}</svg>`,
      hint: 'Modifier les Données',
      onClick: () => {
        this.showForm();
      }
    };

    this.formCancelBtnOptions = {
      text: 'annuler',
      type: 'default',
      stylingMode: 'text',
      onClick: () => {
        this.cancelEdit();
      }
    };

    this.formSaveBtnOptions = {
      text: 'Sauvegarder',
      type: 'default',
      stylingMode: 'text',
      onClick: () => {
        this.saveChanges();
      }
    };
  }

  ngOnInit(): void {
    this.mapReady.pipe(first()).subscribe((map) => {
      this.map = map;
    });

    this.editEventSub = this.editEvent.subscribe(({ layer, field, fieldCrop }) => {
      if (this.hasChanges()) {
        this.shakeToolBar();
        return;
      }
      this.formTrackChanges = false;
      layer.remove();
      this.enableEditing(field, fieldCrop);
    });
  }

  addFormChange(e: any) {
    if (!this.formTrackChanges) {
      return;
    }
    this.changes[e.dataField] = e.value;
  }

  formOnHiding() {
    this.formTrackChanges = false;
  }

  private showForm() {
    const currentCrop = this.items.find(item => {
      return item.id === this.fieldCrop.crop_id
    });

    this.fieldCrop.crop.custom_fields = currentCrop.toColumns(this, 'attributes');

    if (this.formVisible) {
      return;
    }
    this.formTrackChanges = true;
    this.formVisible = true;
  }

  hasChanges(): boolean {
    return Object.keys(this.changes).length !== 0;
  }

  private shakeToolBar() {
    this.shake = true;
    setTimeout(() => {
      this.shake = false;
    }, 820);
  }

  private enableEditing(field: Field, fieldCrop: FieldCrop) {

    this.cleanupEditing();
    if (!fieldCrop.id) {
      this.changes.polygon = field.polygon;
    }

    this.oldField = cloneDeep(field);
    this.oldFieldCrop = cloneDeep(fieldCrop);

    this.field = field;
    this.fieldCrop = fieldCrop;

    this.editLayer = featureGroup();
    this.editLayer.addTo(this.map);

    this.addFieldPolygon();
    this.addPolygon();

    this.editingEnabled = true;
  }

  private disableEditing() {
    if (this.hasChanges()) {
      this.shakeToolBar();
      return;
    }
    this.cleanupEditing();
  }

  cancelEdit(isDeleted = false) {
    this.editLayer.remove();

    this.fieldCrop = this.oldFieldCrop;
    this.field.field_crop = this.fieldCrop;
    this.editDone.emit({ field: this.field, isDeleted });
    this.fieldCrop = null;

    this.formVisible = false;
    this.drawControl(false);
    // @ts-ignore
    this.map.pm.disableGlobalEditMode();
    this.editingEnabled = false;
    this.changes = {};
  }

  private cleanupEditing(isDeleted = false) {
    if (this.fieldCrop) {
      this.editLayer.remove();
      this.field.field_crop = this.fieldCrop;
      this.editDone.emit({ field: this.field, isDeleted });
      this.fieldCrop = null;
    }
    this.formVisible = false;
    this.drawControl(false);
    // @ts-ignore
    this.map.pm.disableGlobalEditMode();
    this.editingEnabled = false;
    this.changes = {};
  }

  private addFieldPolygon() {
    const fieldPolygon = this.field.createLayer({
      // @ts-ignore
      pmIgnore: true,
      dashArray: '30 10',
      weight: 1,
      opacity: 0.5,
      fillOpacity: 0.4,
      color: this.field.color
    });
    this.editLayer.addLayer(fieldPolygon);
  }

  private addPolygon() {
    const layer = this.fieldCrop.layer;
    this.editLayer.addLayer(layer);

    this.addPolygonEditEvents(layer);
  }

  private addPolygonEditEvents(layer: Polygon<any>) {

    layer.on('pm:edit', e => {
      this.fieldCrop.layer = e.layer;
      this.fieldCrop.polygon_area = area(this.fieldCrop.geojson);
      this.changes.polygon = this.fieldCrop.polygon;
      this.changes.polygon_area = this.fieldCrop.polygon_area;
    });

    layer.on('pm:snapdrag', e => {
      this.fieldCrop.polygon_area = area(e.layer.toGeoJSON());
      this.changes.polygon = this.fieldCrop.polygon;
      this.changes.polygon_area = this.fieldCrop.polygon_area;
    });

    layer.on('pm:cut', e => {
      this.fieldCrop.layer = e.layer;

      layer.off('pm:edit');

      this.fieldCrop.polygon_area = area(this.fieldCrop.geojson);

      this.changes.polygon = this.fieldCrop.polygon;
      this.changes.polygon_area = this.fieldCrop.polygon_area;

      this.editLayer.remove();
      this.addPolygon();
    });
  }

  private drawControl(show: boolean) {
    // @ts-ignore
    (this.map.pm.Toolbar.editContainer as HTMLDivElement).style.display = show ? 'block' : 'none';
  }

  private startDrawing() {
    this.drawControl(true);
    // @ts-ignore
    this.fieldCrop.layer.pm.enable();
    // @ts-ignore
    this.map.pm.enableGlobalEditMode({
      snappable: true,
      allowSelfIntersection: true,
    });
  }

  async saveChanges() {
    this.loadingVisible = true;
    let validate = this.form.instance.validate();
    if (validate.status === 'pending') {
      validate = await validate.complete;
    }
    if (!validate.isValid) {
      this.loadingVisible = false;
      this.showForm();
      return;
    }
    const fin = finalize(() => {
      this.loadingVisible = false;
      this.formVisible = false;
      this.changes = {};
      this.drawControl(false);
      // @ts-ignore
      this.map.pm.disableGlobalEditMode();
    });
    if (this.fieldCrop.id) {
      this.fieldCropService.update(this.fieldCrop.id, this.changes as FieldCrop).pipe(fin).subscribe(() => {
        success({
          text: 'Modifications enregistrées avec succès',
          mode: 'light',
        });
      });
    } else {
      const data = Object.assign({}, this.fieldCrop);
      // @ts-ignore
      delete data._layer;
      // @ts-ignore
      delete data._geojson;
      this.fieldCropService.insert(data).pipe(fin).subscribe(id => {
        success({
          text: 'Modifications enregistrées avec succès',
          mode: 'light',
        });
        this.fieldCrop.id = id as number;
      });
    }
  }

  ngOnDestroy(): void {
    this.editEventSub.unsubscribe();
  }

  areaPercent() {
    return (this.fieldCrop?.polygon_area / this.field?.polygon_area * 100).toFixed(1);
  }

  cropChange(id: number) {
    if (!this.formTrackChanges) {
      return;
    }
    this.changes.crop_id = id;
    this.fieldCrop.crop_id = id;

    const currentCrop = this.items.find(item => {
      return item.id === id
    });

    this.fieldCrop.crop.custom_fields = currentCrop.toColumns(this, 'attributes');
  }

  campaignChange(id: number) {
    if (!this.formTrackChanges) {
      return;
    }
    this.changes.campaign_id = id;
    this.fieldCrop.campaign_id = id;
  }
}
